import axiosInstance from './HTTPClient';
const DEFAULT_API_VERSION = import.meta.env.VITE_API_VERSION;
const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface ResponseType {
  data?: any | { data?: any };
  meta?: any;
  message: 'Success' | 'Error';
  success: boolean;
}

class ApiClient {
  private readonly _apiVersion: string = '';
  private readonly _options: any = {};
  private readonly _resource: string;
  private readonly _skipAuth: boolean;

  public constructor(resource: string, options: any = {}, skipAuth: boolean = false) {
    this._apiVersion = `${options.apiVersion || DEFAULT_API_VERSION}`;
    this._options = options;
    this._resource = resource;
    this._skipAuth = skipAuth;
  }

  public get url(): string {
    return `${BASE_URL}/${DEFAULT_API_VERSION}/${this._resource}`;
  }

  // Helper method to merge skipAuth into config
  private mergeConfig(config: any = {}): any {
    return { ...config, skipAuth: this._skipAuth };
  }

  public async get<R = ResponseType>(options: any = {}, resource: string = '', config: any = {}): Promise<R> {
    const handledResource: string = resource && `/${resource}`;
    const mergedConfig = this.mergeConfig(config);
    return axiosInstance.get<any, R>(`${this.url}${handledResource}`, {
      ...mergedConfig,
      params: { ...options },
    });
  }

  public show<R = ResponseType>(id: number | string, config: any = {}): Promise<R> {
    const mergedConfig = this.mergeConfig(config);
    return axiosInstance.get<any, R>(`${this.url}/${id}`, mergedConfig);
  }

  public create<R = ResponseType>(options: any = {}, resource: string = '', config: any = {}): Promise<R> {
    const handledResource: string = resource && `/${resource}`;
    const mergedConfig = this.mergeConfig(config);
    return axiosInstance.post<any, R>(`${this.url}${handledResource}`, options, mergedConfig);
  }

  public update<R = ResponseType>(id: number | string, data: any = {}, config: any = {}): Promise<R> {
    const mergedConfig = this.mergeConfig(config);
    return axiosInstance.patch<any, R>(`${this.url}/${id}`, data, mergedConfig);
  }

  public put<R = ResponseType>(id: number | string, data: any = {}, config: any = {}): Promise<R> {
    const mergedConfig = this.mergeConfig(config);
    return axiosInstance.put<any, R>(`${this.url}/${id}`, data, mergedConfig);
  }

  public delete<R = ResponseType>(id: number | string, resource: string = '', config: any = {}): Promise<R> {
    const handledResource: string = resource && `/${resource}`;
    const mergedConfig = this.mergeConfig(config);
    return axiosInstance.delete<any, R>(`${this.url}${handledResource}/${id}`, mergedConfig);
  }
}

export default ApiClient;
